/**
 * The study directory's model, pure so it is tested without a browser: filter by
 * therapeutic area and phase, sort, page. Built for 20–50 studies (the generator ships
 * three); twelve cards to a page.
 */

export interface DirectoryStudy {
  code: string;
  title: string;
  shortTitle: string;
  phase: string;
  therapeuticArea: string;
  accentColour: string;
  siteCount: number;
  subjectCount: number;
  enrolled: number;
  target: number;
}

export interface DirectoryRoom {
  code: string;
  openQueries: number;
  enrolled?: number;
  target?: number;
  /** How many people are inside the study's rooms (never who). */
  people: number;
}

/** 'portfolio' keeps the order the studies are listed in (the manifest's order). */
export type DirectorySort = 'portfolio' | 'code' | 'active' | 'queries' | 'enrolment';
export const SORTS: DirectorySort[] = ['portfolio', 'code', 'active', 'queries', 'enrolment'];
export const SORT_LABELS: Record<DirectorySort, string> = {
  portfolio: 'Portfolio',
  code: 'Code',
  active: 'People inside',
  queries: 'Open queries',
  enrolment: 'Enrolment',
};

export const ALL = 'All';
export const PAGE_SIZE = 12;

export interface DirectoryQuery {
  area: string;
  phase: string;
  sort: DirectorySort;
  page: number;
}

export const DEFAULT_QUERY: DirectoryQuery = { area: ALL, phase: ALL, sort: 'portfolio', page: 0 };

export interface DirectoryEntry {
  study: DirectoryStudy;
  enrolled: number;
  target: number;
  /** 0..1 */
  progress: number;
  openQueries: number | undefined;
  people: number;
  /** Position in the study list as given. */
  order: number;
}

export interface DirectoryPage {
  entries: DirectoryEntry[];
  /** All studies, and those passing the filters. */
  total: number;
  matching: number;
  /** The page shown (clamped) and how many there are (at least one). */
  page: number;
  pageCount: number;
  /** Filter options, "All" first, then sorted. */
  areas: string[];
  phases: string[];
}

const PHASE_ORDER = ['I', 'II', 'III', 'IV'];

export function directoryOptions(studies: DirectoryStudy[]): { areas: string[]; phases: string[] } {
  const areas = [...new Set(studies.map((s) => s.therapeuticArea))].sort((a, b) =>
    a.localeCompare(b),
  );
  const phases = [...new Set(studies.map((s) => s.phase))].sort(
    (a, b) => PHASE_ORDER.indexOf(a) - PHASE_ORDER.indexOf(b) || a.localeCompare(b),
  );
  return { areas: [ALL, ...areas], phases: [ALL, ...phases] };
}

export function directoryPage(
  studies: DirectoryStudy[],
  rooms: DirectoryRoom[],
  query: DirectoryQuery,
  pageSize = PAGE_SIZE,
): DirectoryPage {
  const byCode = new Map(rooms.map((r) => [r.code, r]));
  const entries: DirectoryEntry[] = studies
    .filter((s) => query.area === ALL || s.therapeuticArea === query.area)
    .filter((s) => query.phase === ALL || s.phase === query.phase)
    .map((study, order) => {
      const room = byCode.get(study.code);
      const enrolled = room?.enrolled ?? study.enrolled;
      const target = room?.target ?? study.target;
      return {
        study,
        enrolled,
        target,
        progress: target > 0 ? Math.min(1, enrolled / target) : 0,
        openQueries: room?.openQueries,
        people: room?.people ?? 0,
        order,
      };
    });
  const byCodeOrder = (a: DirectoryEntry, b: DirectoryEntry): number =>
    a.study.code.localeCompare(b.study.code);
  const sorters: Record<DirectorySort, (a: DirectoryEntry, b: DirectoryEntry) => number> = {
    portfolio: (a, b) => a.order - b.order,
    code: byCodeOrder,
    active: (a, b) => b.people - a.people || byCodeOrder(a, b),
    queries: (a, b) => (b.openQueries ?? -1) - (a.openQueries ?? -1) || byCodeOrder(a, b),
    enrolment: (a, b) => b.progress - a.progress || byCodeOrder(a, b),
  };
  entries.sort(sorters[query.sort]);
  const pageCount = Math.max(1, Math.ceil(entries.length / pageSize));
  const page = Math.min(Math.max(0, query.page), pageCount - 1);
  const { areas, phases } = directoryOptions(studies);
  return {
    entries: entries.slice(page * pageSize, page * pageSize + pageSize),
    total: studies.length,
    matching: entries.length,
    page,
    pageCount,
    areas,
    phases,
  };
}

/** The next option in a cycle (wraps), for the directory's cycling filter buttons. */
export function nextOption<T>(options: readonly T[], current: T, step = 1): T {
  const i = options.indexOf(current);
  const n = options.length;
  return options[(((i < 0 ? 0 : i + step) % n) + n) % n]!;
}
